import { BaseEntity, type EntityDeleteOptions, type ValidationResult } from '@memberjunction/core';
import { RegisterClass } from '@memberjunction/global';
import { FeedbackPlatformNominationEntity, FeedbackPlatformNominationResponseEntity } from 'mj_generatedentities';
import { DecideNominationChange, DecideNominationResponseChange, DecideNominationSeason, DecideOwnedBySaver, DecideParentReadable, type OEPDraftOrSubmitted } from '../rules/decisions.js';
import { CreateOEPRuleData } from '../rules/data.js';
import { IsStaffOrSystem } from '../rules/user.js';
import { OEP_ENTITY } from '../contracts/entities.js';
import { Apply, BeginRules, CurrentString, TrustedID } from './shared.js';

function AsDraftOrSubmitted(v: string | null): OEPDraftOrSubmitted | null {
    return v === 'Draft' || v === 'Submitted' ? v : null;
}

@RegisterClass(BaseEntity, OEP_ENTITY.Nominations)
export class OEPNominationEntity extends FeedbackPlatformNominationEntity {
    override get DefaultSkipAsyncValidation(): boolean { return false; }
    override async ValidateAsync(): Promise<ValidationResult> {
        const result = await super.ValidateAsync();
        const rc = await BeginRules(this, this.ActiveUser, result, 'Nominations', this.ID, {
            SeasonID: this.SeasonID, PresentationID: this.PresentationID, NominatorUserID: this.NominatorUserID,
        }, ['Status']);
        if (!rc) return result;
        if (!this.IsSaved) {
            Apply(result, DecideOwnedBySaver({ IsStaffOrSystem: rc.IsStaff, Field: 'NominatorUserID', OwnerUserID: this.NominatorUserID, SaverUserID: rc.SaverUserID }), this.NominatorUserID);
        }
        const seasonID = TrustedID(rc, 'SeasonID', this.SeasonID);
        const presentation = await rc.Data.Presentation(TrustedID(rc, 'PresentationID', this.PresentationID));
        Apply(result, DecideNominationSeason({
            NominationSeasonID: seasonID, PresentationFound: presentation !== null, PresentationSeasonID: presentation?.SeasonID ?? null,
        }), seasonID);
        const season = await rc.Data.SeasonPhase(seasonID);
        Apply(result, DecideParentReadable({ IsStaffOrSystem: rc.IsStaff, Found: season !== null, Field: 'SeasonID' }), seasonID);
        if (!season) return result;
        const summary = this.IsSaved ? await rc.Data.NominationResponseSummary(this.ID) : { RatedCount: 0, OutstandingWithoutJustification: 0 };
        Apply(result, DecideNominationChange({
            Phase: season.Phase, IsStaffOrSystem: rc.IsStaff,
            // Old status from the fresh DB row, not the client-supplied OldValue.
            OldStatus: AsDraftOrSubmitted(CurrentString(rc, 'Status')),
            NewStatus: this.Status, Attested: this.NoConflictAttested === true, ...summary,
        }), this.Status);
        return result;
    }
}

@RegisterClass(BaseEntity, OEP_ENTITY.NominationResponses)
export class OEPNominationResponseEntity extends FeedbackPlatformNominationResponseEntity {
    override get DefaultSkipAsyncValidation(): boolean { return false; }
    override async ValidateAsync(): Promise<ValidationResult> {
        const result = await super.ValidateAsync();
        const rc = await BeginRules(this, this.ActiveUser, result, 'NominationResponses', this.ID, { NominationID: this.NominationID, ReviewQuestionID: this.ReviewQuestionID });
        if (!rc) return result;
        const n = await rc.Data.Nomination(TrustedID(rc, 'NominationID', this.NominationID));
        const season = n ? await rc.Data.SeasonPhase(n.SeasonID) : null;
        Apply(result, DecideParentReadable({ IsStaffOrSystem: rc.IsStaff, Found: season !== null, Field: 'NominationID' }), this.NominationID);
        if (!n || !season) return result;
        Apply(result, DecideNominationResponseChange({ Phase: season.Phase, NominationStatus: n.Status }), this.NominationID);
        return result;
    }

    /**
     * MJ runs no validation on Delete, and OEP Nominator holds CanDelete here, so the window is
     * enforced in the BaseEntity.Delete override. The delete resolver loads the row from the DB first
     * (server ResolverBase.js:1217), and the parent Nomination is read fresh as the saver.
     */
    override async Delete(options?: EntityDeleteOptions): Promise<boolean> {
        const user = this.ActiveUser;
        if (!IsStaffOrSystem(user)) {
            const data = CreateOEPRuleData(this.RunViewProviderToUse, user);
            const n = await data.Nomination(this.NominationID);
            const season = n ? await data.SeasonPhase(n.SeasonID) : null;
            const readable = DecideParentReadable({ IsStaffOrSystem: false, Found: season !== null, Field: 'NominationID' });
            if (!readable.Ok) throw new Error(readable.Message);
            if (n && season) {
                const d = DecideNominationResponseChange({ Phase: season.Phase, NominationStatus: n.Status });
                if (!d.Ok) throw new Error(d.Message);
            }
        }
        return super.Delete(options);
    }
}
